import React, { useEffect, useState } from "react";
import { Row, Col, Typography, Divider, Table, Spin } from "antd";
import { useSelector } from "react-redux";
import Big from "big.js";

import {
  convertNumberToWords,
  formatToCompanyTimezone,
  numberFormat,
  numberFormatTax,
} from "../../../lib/functions";

import { salesQuotationWithRecordApi } from "../../../apiservice/sales";
import { getInstitutionApi } from "../../../apiservice/setting";

import "./voucher-mobile.css";

const { Title, Text } = Typography;

const QuotationVoucherBody = ({
  id,
  printMode = false,
  showStamp = false,
  showSignature = false,
  setVoucherNo,
  setAccName,
  setCustomerEmail,
}) => {
  const authInfo = useSelector((s) => s.auth.auth_info);

  const [voucher, setVoucher] = useState(null);
  const [institution, institutionSet] = useState(null);

  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const taxLabel = isIndia ? "GST" : "VAT";
  const gstEnabled = authInfo?.gst_enabled === "yes";
  const showTax = !isIndia || gstEnabled;

  useEffect(() => {
    if (!id) return;

    salesQuotationWithRecordApi({
      sale_o_id: id,
      from: "voucher",
    }).then((res) => {
      if (res?.data?.length > 0) {
        const voucherData = res.data[0];

        setVoucher(voucherData);

        setVoucherNo?.(voucherData.sale_order_no || "");
        setAccName?.(voucherData.acc_name || "");
        setCustomerEmail?.(
          voucherData.email_address ||          
          ""
        );
      }
    });

    getInstitutionApi().then((res) => {
      if (res?.data) {
        institutionSet(res.data);
      }
    });
  }, [
  id,
  setVoucherNo,
  setAccName,
  setCustomerEmail,
]);

  if (!voucher) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <Spin />
      </div>
    );
  }


    // Terms Section starts
      const TermsSection = ({ voucher }) => {
      const renderBulletText = (text) => {
        if (!text) return null;

        return (
          <ul
            style={{
              paddingLeft: 24,
              marginTop: 8,
              marginBottom: 24,
            }}
          >
            {text
              .split("\n")
              .filter((line) => line.trim())
              .map((line, index) => (
                <li
                  key={index}
                  style={{
                    marginBottom: 10,
                    lineHeight: 1.8,
                    fontSize: 14,
                  }}
                >
                  {line}
                </li>
              ))}
          </ul>
        );
      };

      if (
        !voucher?.terms_conditions &&
        !voucher?.payment_terms
      ) {
        return null;
      }

      return (
        <div
          style={{
            padding: "20px 10px",
          }}
        >
          {voucher?.terms_conditions && (
            <>
              <h2
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  marginBottom: 12,
                  textAlign: "center",
                }}
              >
                Terms & Conditions
              </h2>

              {renderBulletText(voucher.terms_conditions)}
            </>
          )}

          {voucher?.payment_terms && (
            <>
              <h2
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  marginBottom: 12,
                  textAlign: "center",
                }}
              >
                Payment Terms
              </h2>

              {renderBulletText(voucher.payment_terms)}
            </>
          )}
        </div>
      );
    };

    // Terms Section ends

  const details = voucher.details || [];

  const grandTotal = Big(voucher.total_amount || 0)
    .times(voucher.conversion_rate || 1)
    .toNumber();

  const totalTax = Number(voucher.total_tax || 0);

  const columns = [
    {
      title: "NO.",
      render: (_, __, index) => index + 1,
      width: 40,
    },
    {
      title: "ITEM NAME",
      dataIndex: "item_name",
      render: (_, record) => (
        <>
          <div>{record.item_name}</div>

          {record.item_description && (
            <div
              style={{
                fontSize: 11,
                color: "#666",
                marginTop: 4,
                whiteSpace: "pre-wrap",
              }}
            >
              {record.item_description}
            </div>
          )}
        </>
      ),
    },
    {
      title: "RATE",
      dataIndex: "item_rate",
      align: "right",
      width: 70,
      render: (value) => Big(value || 0).toFixed(2),
    },
    {
      title: "PER",
      dataIndex: "unit_symbol",
      align: "center",
      width: 70,
    },
    {
      title: "QUANTITY",
      dataIndex: "item_qty",
      align: "right",
      width: 90,
    },
    {
      title: "TOTAL",
      dataIndex: "item_total",
      align: "right",
      width: 90,
      render: (value) => Big(value || 0).toFixed(2),
    },
  ];

  return (
    <div
      data-testid="quotation-voucher-card"
      className="invoice-v2 voucher-section"
    >
      {/* =========================
          HEADER
      ========================= */}

      <Row
        justify="space-between"
        align="top"
        className="invoice-header"
      >
        <Col
          flex="0 0 320px"
          className="bill-to"
          style={{ marginTop: "58px" }}
        >
          <Text strong>
            Customer/ Debtor Code: &nbsp;{voucher.acc_code}
          </Text>

          <br />

          <Text strong>
            Name: &nbsp;{voucher.acc_name}
          </Text>

          <br />

          <Text className="wrap-text">
            Address: &nbsp;{voucher.address || "-"}
          </Text>

          <br />

          {voucher.contact_no && (
            <Text>
              Contact No: &nbsp;+{voucher.contact_no}
            </Text>
          )}
        </Col>

        <Col flex="320px" className="invoice-title">
          <Title level={2}>QUOTATION</Title>

          <Text strong>
            Voucher No: {voucher.sale_order_no}
          </Text>

          <br />

          <Text>
            Quotation Date:{" "}
            {
              formatToCompanyTimezone(voucher.created_date)?.split(
                " "
              )[0]
            }
          </Text>

          <br />

          <Text>
            Quotation By: {voucher.full_name}
          </Text>

          <br />

          <Text className="wrap-text">
            Address: {institution?.street_address || "-"}
          </Text>

          <br />

          {institution?.phone_no && (
            <>
              <Text>
                Contact No: &nbsp;+{institution.phone_no}
              </Text>

              <br />
            </>
          )}

          {institution?.vat_license && (
            <>
              <Text>
                {institution?.country
                  ?.trim()
                  .toLowerCase() === "united arab emirates"
                  ? "TRN No: "
                  : "Tax Reg No: "}

                {institution.vat_license}
              </Text>

              <br />
            </>
          )}
        </Col>
      </Row>

      {/* =========================
          ITEMS
      ========================= */}

      <Table
        className="invoice-items"
        columns={columns}
        dataSource={details}
        pagination={false}
        rowKey={(record) =>
          record.item_id || record.sale_o_d_id
        }
        size="small"
      />

      {/* =========================
          TOTAL SECTION
      ========================= */}

      <Divider />

      <Row>
        <Col span={16} />

        <Col span={8} style={{ textAlign: "right" }}>
          <div>
            <div
              style={{
                display: "flex",
                textAlign: "left",
              }}
            >
              <Text>Quantity Total :</Text>

              <Text>
                (
                {details.reduce(
                  (sum, item) =>
                    sum + (Number(item.item_qty) || 0),
                  0
                )}
                )
              </Text>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Text>Sub Total :</Text>

              <Text>
                {numberFormat(voucher.sub_total || 0)}
              </Text>
            </div>

            {voucher.is_cal_type === "on_total" && showTax && (
              <>
                {isIndia ? (
                  <>
                    {voucher.place_of_supply_type === "intra" && (
                      <>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Text>CGST :</Text>

                          <Text>
                            {numberFormatTax(
                              voucher.cgst_amount || 0
                            )}
                          </Text>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Text>SGST :</Text>

                          <Text>
                            {numberFormatTax(
                              voucher.sgst_amount || 0
                            )}
                          </Text>
                        </div>
                      </>
                    )}

                    {voucher.place_of_supply_type === "inter" && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Text>IGST :</Text>

                        <Text>
                          {numberFormatTax(
                            voucher.igst_amount || 0
                          )}
                        </Text>
                      </div>
                    )}
                  </>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Text>{taxLabel} :</Text>

                    <Text>
                      {numberFormat(totalTax)}
                    </Text>
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Text>Discount :</Text>

                  <Text>
                    {numberFormat(
                      voucher.total_discount || 0
                    )}
                  </Text>
                </div>
              </>
            )}

            {Big(voucher.other_service_cost || 0).gt(0) && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Text>Additional Expenses :</Text>

                <Text>
                  {numberFormat(
                    voucher.other_service_cost || 0
                  )}
                </Text>
              </div>
            )}

            <div
              style={{
                borderTop: "1px solid #000",
                display: "flex",
                justifyContent: "space-between",
                paddingTop: 4,
                marginTop: 4,
              }}
            >
              <Text strong>
                Grand Total (
                {institution?.currency ||
                  authInfo?.currency ||
                  "AED"}
                ):
              </Text>

              <Text strong>
                {numberFormat(grandTotal)}
              </Text>
            </div>
          </div>
        </Col>
      </Row>

      {/* =========================
          FOOTER
      ========================= */}

      <Divider />

      <Row>
        <Col span={24}>
          <Text>
            In Word of Grand Total :{" "}
            {convertNumberToWords(
              grandTotal,
              authInfo?.currency ||
                institution?.currency ||
                "AED"
            )}
          </Text>

          <br />

          <Text>
            Description : {voucher.narration || "-"}
          </Text>
        </Col>
      </Row>

      {(showStamp || showSignature) && (
        <div className="invoice-footer-section">
          <div className="footer-right-group">
            {showStamp && institution?.stamp && (
              <div className="stamp-block">
                <img src={institution.stamp} alt="Stamp" />
              </div>
            )}

            {showSignature && institution?.signature && (
              <div className="signature-block">
                <img src={institution.signature} alt="Signature" />
                <div className="sign-line-text">Authorized Signature</div>
              </div>
            )}
          </div>
        </div>
      )}

        {/* Terms & Condition */}
        {voucher.show_terms_conditions === "yes" && (
          <div
            style={{
              pageBreakBefore: "always",
              paddingTop: "20px",
            }}
          >
            <TermsSection voucher={voucher} />
          </div>
        )}


    </div>
  );
};

export default QuotationVoucherBody;